'use client';

import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@pickleballinc/ui/components/ui/select';
import { ReadonlyURLSearchParams, useRouter, useSearchParams } from 'next/navigation';

import { NavTabs } from '@/components/molecules';
import { NoDataMessage } from '@/components/molecules/NoDataMessage';
import { TeamInfoData } from '@/components/organisms';
import { TeamInfoTableSkeleton } from '@/components/organisms/skeletons/TeamInfoTableSkeleton';
import { Result } from '@/entities';
import { PermissionRole } from '@/entities/PermissionRoles';
import { SeasonListItem } from '@/entities/SeasonList';
import { Currency } from '@/entities/shared/lookups/Currency';
import { ActivityTeamInfoData, TeamInfoData as TeamInfoDataType } from '@/entities/Team';
import { TeamLeagueInfo } from '@/entities/TeamLeagueInfo';
import { useSeasonDistrictDivisionInfo } from '@/hooks/custom-hooks/useSeasonDistrictDivisionInfo';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { useActiveTeams, usePlayerActivity, useSeasonDivisions } from '@/hooks/hooks';
import { useSeasonDivisionPool } from '@/hooks/leagueSeasonDivision/useSeasonDivisionPool';
import { useTeamLeagueSeasonDivision } from '@/hooks/leagueSeasonDivision/useTeamLeagueSeasonDivision';
import { useCurrenciesQuery } from '@/hooks/lookups/useCurrencyQuery';
import { useOrganizationStore } from '@/store/organization-store';
import { usePermissionStore } from '@/store/permission-store';
import { getDistrictOptions } from '@/utils/utils';

interface DivisionDataProps {
	teamLeagueId: string;
	type: 'active-teams' | 'player-activity';
}

export const DivisionInfoData = ({ teamLeagueId, type }: DivisionDataProps) => {
	const permissionStore = usePermissionStore();
	const teamLeagueInfo = useTeamLeagueInfo();
	const store = useOrganizationStore();
	const searchParams = useSearchParams();
	const router = useRouter();

	const { seasonsInfo, seasonOptions, firstSeasonOptionUuid, districtsInfo, firstDistrictOptionUuid } = useSeasonDistrictDivisionInfo();

	const shouldHideData = permissionStore.userPermission === PermissionRole.CAN_MANAGE_GAMEDAYS;

	const selectedSeason = searchParams.get('season') || firstSeasonOptionUuid;

	const districtOptions = getDistrictOptions(districtsInfo, true);
	const districtsLength = districtsInfo.length;

	const selectedDistrict = searchParams.get('district') || (districtsLength === 1 ? firstDistrictOptionUuid : undefined);

	const { data: divisionsInfoData, isLoading: isLoadingDivisions } = useSeasonDivisions(
		store.organizationUuid,
		store.teamLeagueUuid,
		selectedSeason || ''
	);

	const selectedDivision =
		searchParams.get('division') ||
		(divisionsInfoData?.data?.teamLeaguesDivisions?.length === 1 ? divisionsInfoData?.data?.teamLeaguesDivisions[0]?.uuid : '');

	// We fetch this API only to get the season division uuid and put it in the pool data api call
	const { data: seasonDivision, isLoading: isLoadingSeasonDivision } = useTeamLeagueSeasonDivision({
		seasonUuid: selectedSeason || '',
		districtUuid: selectedDistrict || '',
		divisionUuid: selectedDivision || ''
	});

	const { data: poolData, isLoading: isLoadingPoolData } = useSeasonDivisionPool(seasonDivision?.data?.uuid || '');
	const poolId = poolData?.data ? poolData.data : '';

	const { data: playerActivityData, isLoading: isPlayerActivityLoading } = usePlayerActivity({
		selectedSeason: selectedSeason || '',
		disabled: type === 'active-teams',
		searchParams
	});

	const { data: activeTeamsData, isLoading: isActiveTeamsLoading } = useActiveTeams({
		selectedSeason: selectedSeason || '',
		disabled: type === 'player-activity',
		searchParams
	});

	const { data: currencies, isLoading: isLoadingCurrencies } = useCurrenciesQuery(true);

	const isLoadingSeasonDivisionData = isLoadingDivisions || isLoadingSeasonDivision;
	const isLoadingPageContent = isPlayerActivityLoading || isActiveTeamsLoading || isLoadingCurrencies;

	const data = type === 'player-activity' ? playerActivityData?.data : activeTeamsData?.data;

	const divisionOptions = [
		{
			label: 'All',
			value: 'all'
		},
		...(divisionsInfoData?.data.teamLeaguesDivisions || []).map((division) => ({
			label: division.title,
			value: division.uuid
		}))
	];

	const handleSeasonChange = (selectedOption: string) => {
		const params = new URLSearchParams();

		if (selectedOption) {
			params.set('season', selectedOption);
		}

		params.delete('division');
		params.delete('district');
		router.replace(`?${params.toString()}`, { scroll: false });
	};

	const handleDistrictChange = (selectedOption: string) => {
		const params = new URLSearchParams(searchParams.toString());

		if (selectedOption) {
			params.set('district', selectedOption);
		}
		if (selectedSeason) {
			params.set('season', selectedSeason);
		}

		router.replace(`?${params.toString()}`, { scroll: false });
	};

	const handleDivisionChange = (selectedOption: string) => {
		const params = new URLSearchParams(searchParams.toString());

		if (selectedOption) {
			params.set('division', selectedOption);
		}
		if (selectedSeason) {
			params.set('season', selectedSeason);
		}

		router.replace(`?${params.toString()}`, { scroll: false });
	};

	const activeTeams = () => {
		if (!selectedDivision || selectedDivision === 'all') {
			return (data || []).flatMap((division: any) => division.teams || []);
		} else {
			const division = (data || []).find((div) => div.uuid === selectedDivision);
			return division ? division.teams : [];
		}
	};

	if (!isLoadingSeasonDivisionData && !seasonOptions.length) {
		return <NoDataMessage title="The season hasn’t started." description=" No matches in play at the moment-stay tuned, the action is coming!" />;
	}

	return (
		<div className="mt-3">
			<div>
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6 w-full">
					<div className="flex flex-col">
						<label htmlFor="district-select" className="mb-1 text-sm">
							Season
						</label>
						<Select onValueChange={(value) => handleSeasonChange(value)} value={selectedSeason || firstSeasonOptionUuid}>
							<SelectTrigger
								id="season-select"
								className="bg-white focus:ring-0 w-full"
								icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
							>
								<SelectValue placeholder="Select season" />
							</SelectTrigger>
							<SelectContent>
								{seasonOptions.map((season) => (
									<SelectItem key={season.value} value={season.value}>
										{season.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="hidden sm:block" />

					{districtsLength > 1 && (
						<div className="flex flex-col">
							<label htmlFor="district-select" className="mb-1 text-sm">
								District
							</label>
							<Select onValueChange={(value) => handleDistrictChange(value)} value={selectedDistrict || districtOptions[0]?.value}>
								<SelectTrigger
									id="district-select"
									className="bg-white focus:ring-0 w-full"
									icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
								>
									<SelectValue placeholder="Select district" />
								</SelectTrigger>
								<SelectContent>
									{districtOptions.map((district) => (
										<SelectItem key={district.value} value={district.value}>
											{district.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					)}

					{!!divisionsInfoData && divisionsInfoData?.data.teamLeaguesDivisions.length > 1 && (
						<div className="flex flex-col">
							<label htmlFor="division-select" className="mb-1 text-sm">
								Division
							</label>
							<Select onValueChange={(value) => handleDivisionChange(value)} value={selectedDivision || divisionOptions[0]?.value}>
								<SelectTrigger
									id="division-select"
									className="bg-white focus:ring-0 w-full"
									icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
								>
									<SelectValue placeholder="Select division" />
								</SelectTrigger>
								<SelectContent>
									{divisionOptions.map((division) => (
										<SelectItem key={division.value} value={division.value}>
											{division.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					)}
				</div>

				<NavTabs
					teamLeagueId={teamLeagueId}
					seasonId={selectedSeason}
					districtId={selectedDistrict || 'all'}
					districtCount={districtsLength}
					divisionId={selectedDivision || 'all'}
					divisionCount={divisionsInfoData ? divisionsInfoData.data.teamLeaguesDivisions.length : 0}
					poolId={poolId}
					isLoadingPoolData={isLoadingPoolData}
				/>
			</div>

			{isLoadingPageContent ? (
				<TeamInfoTableSkeleton />
			) : (
				<DivisionInfoDataContent
					shouldHideData={shouldHideData}
					hasDivision={Boolean(divisionsInfoData && divisionsInfoData.data.teamLeaguesDivisions.length > 0)}
					activeTeams={activeTeams()}
					teamLeagueId={teamLeagueId}
					selectedSeason={selectedSeason}
					teamLeagueInfo={teamLeagueInfo}
					currencies={currencies}
					searchParams={searchParams}
					selectedDivision={selectedDivision}
					seasonsList={seasonsInfo}
				/>
			)}
		</div>
	);
};

interface DivisionInfoContentProps {
	shouldHideData: boolean;
	hasDivision: boolean;
	activeTeams: any[];
	teamLeagueId: string;
	selectedSeason?: string;
	teamLeagueInfo?: TeamLeagueInfo;
	currencies?: Result<Currency[]>;
	searchParams?: ReadonlyURLSearchParams;
	selectedDivision?: string;
	seasonsList: SeasonListItem[];
}
const DivisionInfoDataContent = ({
	shouldHideData,
	hasDivision,
	activeTeams,
	teamLeagueId,
	selectedSeason,
	teamLeagueInfo,
	currencies,
	searchParams,
	seasonsList
}: DivisionInfoContentProps) => {
	if (shouldHideData) {
		return (
			<NoDataMessage
				title="Heads up!"
				description={`Looks like you don’t have access to this page. Need help? Contact your league organizer.`}
			/>
		);
	}

	if (!hasDivision) {
		return (
			<NoDataMessage title="No rally here!" description="Looks like there's nothing to show right now. Check back soon—we'll be warming up!" />
		);
	}

	if (activeTeams.length > 0) {
		return (
			<div className="flex flex-col gap-4">
				{activeTeams.map((team: ActivityTeamInfoData | TeamInfoDataType, index: number) => (
					<TeamInfoData
						key={`${team.uuid}-${team.players.length}-${index}`}
						teamLeagueId={teamLeagueId}
						teamId={team.uuid}
						players={team.players}
						name={team.name}
						seasonId={selectedSeason || ''}
						isFreeAgents={team.allowFreeAgents}
						teamPaysRegistration={(team as ActivityTeamInfoData).teamPaysRegistration}
						hasCaptainPaid={(team as ActivityTeamInfoData).hasCaptainPaid}
						balance={(team as ActivityTeamInfoData).balance}
						cartUuid={(team as ActivityTeamInfoData).cartUuid}
						leagueInfo={teamLeagueInfo}
						currencies={currencies}
						invitationCode={team.code}
						seasonData={seasonsList.find((season: SeasonListItem) => season.uuid === selectedSeason) || null}
					/>
				))}
			</div>
		);
	}

	if (activeTeams.length === 0) {
		return (
			<NoDataMessage
				title={searchParams ? 'Nothing in the Kitchen!' : 'No rally here!'}
				description={
					searchParams
						? 'We couldn’t find any results that matched—try adjusting your search or filters and serve it up again!'
						: "Looks like there's nothing to show right now. Check back soon—we'll be warming up!"
				}
			/>
		);
	}

	return null;
};
